import * as React from "react";
import { cn } from "../lib/cn";
import { statusDisplay, type StatusInput, type Tone } from "../lib/status";
import { Tooltip } from "../primitives/menu";

const dotTone: Record<Tone, string> = {
  neutral: "bg-neutral-dot",
  success: "bg-success-dot",
  warning: "bg-warning-dot",
  danger: "bg-danger-dot",
};


export interface StatusProps extends StatusInput {
  message?: string;
  since?: string;
  className?: string;
}

function tooltipText(p: StatusProps, phase: string) {
  return [`phase: ${phase}`, p.reason && `reason: ${p.reason}`, p.message, p.since && `since ${p.since}`]
    .filter(Boolean)
    .join(" · ");
}

/** List status: a 6px dot and the display word; danger color appears only on the dot (ui-spec §3.1). */
export function StatusDot(props: StatusProps & { showDetail?: boolean }) {
  const d = statusDisplay(props);
  return (
    <Tooltip content={tooltipText(props, d.phase)}>
      <span className={cn("inline-flex items-center gap-1.5 text-ui whitespace-nowrap text-fg-secondary", props.className)}>
        <span className={cn("nd-dot", dotTone[d.tone])} data-transitional={d.transitional} aria-hidden />
        <span>{d.word}</span>
        {props.showDetail && d.detail ? <span className="text-fg-muted">· {d.detail}</span> : null}
      </span>
    </Tooltip>
  );
}

/** Header status: the dot and the word in the status line, tooltip with the raw phase (ui-spec §3.3). */
export function StatusPill(props: StatusProps) {
  const d = statusDisplay(props);
  return (
    <Tooltip content={tooltipText(props, d.phase)}>
      <span data-testid="status-pill" className={cn("inline-flex items-center gap-1.5 text-ui text-fg-body", props.className)}>
        <span className={cn("nd-dot", dotTone[d.tone])} data-transitional={d.transitional} aria-hidden />
        {d.word}
        {d.detail ? <span className="text-fg-muted">· {d.detail}</span> : null}
      </span>
    </Tooltip>
  );
}

/** Alpha and Beta come from discovery or a field's stage, never hard-coded per page (ui-spec §4.7). */
export function StageBadge({ stage, children }: { stage: "Alpha" | "Beta"; children?: React.ReactNode }) {
  return (
    <span className="inline-flex h-5 items-center rounded-sm border border-line px-1.5 text-small text-fg-muted">
      {stage}
      {children ? <span className="ml-1 text-fg-muted">· {children}</span> : null}
    </span>
  );
}

const categoryTones = {
  category: "border-category-border bg-category-bg text-category-text",
  mode: "border-transparent bg-hover text-fg-secondary",
  modality: "border-line bg-card text-fg-secondary",
} as const;

export function CategoryBadge({ tone, children }: { tone: keyof typeof categoryTones; children: React.ReactNode }) {
  return (
    <span className={cn("inline-flex h-5 items-center rounded-sm border px-1.5 text-badge", categoryTones[tone])}>
      {children}
    </span>
  );
}
